import { AtSign, Clock, ExternalLink, Mail, MessageSquare, PawPrint, Phone } from 'lucide-react'

import { type CommunicationWithPatient, formatCommunicationType } from '@snootsvet/shared'

import { FormattedTime } from '@/frontend/components/formatted-datetime'
import { Button } from '@/frontend/components/ui/button'

const CHANNEL_LABELS: Record<CommunicationWithPatient['channel'], string> = {
  email: 'Email',
  sms: 'SMS',
}

/**
 * Timeline entry card for a message we sent a client. Shared by the member and
 * patient timelines.
 *
 * Metadata only, deliberately: the body is rendered by the Customer.io template and
 * never stored here, so the card answers "what were they told, and when" rather than
 * reproducing the text.
 */
export function CommunicationEntry({
  communication,
  showPatient = false,
}: {
  communication: CommunicationWithPatient
  /** Off on the patient timeline, where the pet is already the page. */
  showPatient?: boolean
}) {
  return (
    <div className="rounded-xl py-3 bg-communication/3 ring-1 ring-communication/33 text-card-foreground text-sm">
      <div className="space-y-1 px-4">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-balance">
            {formatCommunicationType(communication.type)}
          </span>
          {communication.externalUrl && (
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="View message"
              className="ml-auto -my-1"
              render={
                <a href={communication.externalUrl} target="_blank" rel="noopener noreferrer" />
              }
            >
              <ExternalLink className="size-3.5" />
            </Button>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-muted-foreground">
          <span className="inline-flex items-center gap-1">
            {communication.channel === 'email' ? (
              <Mail className="size-3 shrink-0" />
            ) : (
              <MessageSquare className="size-3 shrink-0" />
            )}
            {CHANNEL_LABELS[communication.channel]}
          </span>
          <span className="inline-flex items-center gap-1">
            {communication.channel === 'email' ? (
              <AtSign className="size-3 shrink-0" />
            ) : (
              <Phone className="size-3 shrink-0" />
            )}
            {communication.toAddress}
          </span>
          <span className="inline-flex items-center gap-1">
            <Clock className="size-3 shrink-0" />
            <FormattedTime value={communication.sentAt} clinicId={null} />
          </span>
          {showPatient && communication.patient && (
            <span className="inline-flex items-center gap-1">
              <PawPrint className="size-3 shrink-0" />
              {communication.patient.name}
            </span>
          )}
        </div>
      </div>
    </div>
  )
}
